<section
  class="bn-outcomes"
  aria-label="Case outcomes"
  [attr.aria-busy]="store.loading() || opening()"
>
  @if (store.loading()) {
    <div role="status" aria-label="Loading outcomes">
      <p-skeleton height="5rem" /><p-skeleton
        width="12rem"
        height="2.25rem"
        styleClass="mt-3"
      />
    </div>
  } @else if (store.error(); as error) {
    <div class="bn-outcomes__notice" role="alert">
      <p>{{ error }}</p>
      <button type="button" class="bn-outcomes__retry" (click)="store.load()">
        Retry outcomes
      </button>
    </div>
  } @else if (store.data()) {
    <div class="bn-outcomes__impact">
      <div>
        <h3>Total financial impact</h3>
        <strong>{{
          store.totalAti() | currency: 'USD' : 'symbol' : '1.0-2'
        }}</strong>
      </div>
      @if (store.canWrite()) {
        @if (store.referenceLoading() || opening()) {
          <div
            role="status"
            [attr.aria-label]="
              opening() ? 'Opening outcome' : 'Loading outcome choices'
            "
            data-testid="outcome-choices-skeleton"
          >
            <p-skeleton width="9rem" height="2.25rem" />
          </div>
        } @else {
          <button
            type="button"
            class="bn-outcomes__record"
            [disabled]="
              opening() ||
              store.saving() ||
              store.referenceLoading() ||
              !!store.referenceError() ||
              !store.types().length ||
              !store.options().length
            "
            (click)="openEditor()"
          >
            <i class="pi pi-plus" aria-hidden="true"></i>
            Record outcome
          </button>
        }
      }
    </div>
    @if (store.referenceError(); as error) {
      <div class="bn-outcomes__notice" role="alert">
        <p>{{ error }}</p>
        <button
          type="button"
          class="bn-outcomes__retry"
          (click)="store.loadReferences(true)"
        >
          Retry outcome choices
        </button>
      </div>
    } @else if (
      !store.referenceLoading() && store.canWrite() && !store.options().length
    ) {
      <p class="bn-outcomes__hint">
        Add an option in the Plan tab before recording an outcome.
      </p>
    }
    @if (!store.canWrite()) {
      <p class="bn-outcomes__hint">
        Outcomes are view-only for this case or your current access.
      </p>
    }
    @if (openError(); as error) {
      <div class="bn-outcomes__notice" role="alert">
        <p>{{ error }}</p>
        <button type="button" class="bn-outcomes__retry" (click)="store.load()">
          Refresh outcomes
        </button>
      </div>
    }
    @if (store.outcomes().length === 0) {
      <p class="bn-outcomes__empty">Nothing recorded yet.</p>
    } @else {
      <div class="bn-outcomes__list">
        @for (outcome of store.outcomes(); track outcome.outcomeId) {
          @let title = store.title(outcome);
          <article
            class="bn-outcomes__item"
            [class.bn-outcomes__item--removed]="outcome.invalidated"
            (mouseenter)="showDetails(outcome.outcomeId)"
            (mouseleave)="scheduleHideDetails()"
          >
            <div
              class="bn-outcomes__pill"
              cdkOverlayOrigin
              #origin="cdkOverlayOrigin"
            >
              <button
                type="button"
                class="bn-outcomes__details-trigger"
                (focusin)="expanded.set(outcome.outcomeId)"
                (focusout)="hideDetailsOnBlur($event)"
                (keydown.escape)="expanded.set(null)"
                [attr.aria-label]="
                  (store.canEdit(outcome) ? 'Edit outcome: ' : 'Outcome: ') +
                  title +
                  (outcome.invalidated ? ' (removed)' : '')
                "
                [attr.aria-describedby]="
                  'outcome-details-' + caseId() + '-' + outcome.outcomeId
                "
                (click)="openEditor(outcome)"
              >
                <span class="bn-outcomes__type-label">{{
                  title.length > 50 ? (title | slice: 0 : 50) + '…' : title
                }}</span>
                @if (outcome.invalidated) {
                  <span class="bn-outcomes__removed">Removed</span>
                }
                @if (store.canEdit(outcome)) {
                  <i
                    class="pi pi-pencil bn-outcomes__edit"
                    aria-hidden="true"
                  ></i>
                }
              </button>
            </div>
            <ng-template
              cdkConnectedOverlay
              cdkConnectedOverlayPanelClass="bn-outcome-details-overlay"
              [cdkConnectedOverlayOrigin]="origin"
              [cdkConnectedOverlayOpen]="expanded() === outcome.outcomeId"
              [cdkConnectedOverlayPositions]="detailPositions"
              [cdkConnectedOverlayPush]="true"
              [cdkConnectedOverlayViewportMargin]="8"
              (overlayKeydown)="$event.key === 'Escape' && expanded.set(null)"
            >
              <div
                class="bn-outcomes__details"
                role="tooltip"
                [id]="'outcome-details-' + caseId() + '-' + outcome.outcomeId"
              >
                <p class="bn-outcomes__description">
                  {{ title }}
                </p>
                @if (outcome.description) {
                  <p>{{ outcome.description }}</p>
                }
                <p>{{ store.label(outcome.outcomeTypeCode) }}</p>
                <p>
                  ATI:
                  {{
                    outcome.atiValue === null
                      ? 'N/A'
                      : (outcome.atiValue
                        | currency: 'USD' : 'symbol' : '1.0-2')
                  }}
                </p>
                @for (
                  option of outcome.linkedOptions;
                  track option.linkedOptionId
                ) {
                  <p>
                    <i class="pi pi-link" aria-hidden="true"></i>
                    {{ option.linkedOptionLabel }}
                  </p>
                }
                <p>
                  Recorded {{ outcome.recordedAt | date: 'mediumDate' }} by
                  {{ outcome.recordedBy.displayName }}.
                </p>
                @if (outcome.invalidated) {
                  <p>
                    <strong>Reason for removal:</strong>
                    {{ outcome.invalidatedReason }}
                  </p>
                }
              </div>
            </ng-template>
          </article>
        }
      </div>
    }
  } @else {
    <p class="bn-outcomes__hint" role="status">
      Waiting for the selected client’s case details…
    </p>
  }
</section>
